import React, { memo } from "react";
import { useHighlightedCitiesQuery } from "@/hooks/queries/useHighlightedCitiesQuery";
import QueryRequest from "_c/QueryRequest";
import isempty from "lodash/isEmpty";
import AngleRightIcon from "_c/icon/AngleRight";
import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useSearchUrlGenerator } from "@/hooks/useSearchUrlGenerator";
import * as S from "./styled";
import SearchIcon from "_c/icon/Search";
import { Button } from "@/components/button";
import ArrowLeftIcon from "_c/icon/ArrowLeft";
import { CitiesAPI } from "@/api/cities";
import BrowserOnly from "@/components/BrowserOnly";
import { AsyncSelect } from "@/components/Select";

type tProps = {
  className?: string;
  listClassName?: string;
  searchClassName?: string;
  enableSearch?: boolean;
  showGoBackButton?: boolean;
  onGoBackClick?: () => any;
};
const ns = "common:masters_search_city_picker.";
const HighlightedCities = (props: tProps) => {
  const { t, ready } = useTranslation(["common", "errors"], {
    useSuspense: false,
  });
  const history = useHistory();
  const query = useHighlightedCitiesQuery();
  const cities = query.data;
  if (!ready) return null;
  return (
    <div className={props.className}>
      {props.showGoBackButton && (
        <div
          css={`
            display: none;
            @media (max-width: ${({ theme }: any) => theme.mobileBp}) {
              display: block;
            }
          `}
        >
          <Button
            css={`
              font-weight: 500;
              padding-left: 10px;
            `}
            transparent
            withIcon
            small
            onClick={() => {
              if (props.onGoBackClick) {
                props.onGoBackClick();
              } else {
                history.goBack();
              }
            }}
          >
            <ArrowLeftIcon width="14" color="currentColor" />
            <span>{t(ns + "title")}</span>
          </Button>
        </div>
      )}
      {props.enableSearch && (
        <BrowserOnly>
          <Search className={props.searchClassName} t={t} />
        </BrowserOnly>
      )}
      <QueryRequest query={query}>
        {!isempty(cities) && (
          <S.List className={props.listClassName}>
            {cities!.map((city) => (
              <li key={city._id}>
                <Link
                  css={`
                    color: var(--font-color-accent);
                  `}
                  to={`/search/${city.slug}`}
                >
                  <span>{city.name}</span>
                  <AngleRightIcon
                    width="6"
                    css={`
                    display: none;
              @media (max-width: ${({ theme }: any) => theme.mobileBp}) {
                display: block;
                  `}
                    color="currentColor"
                  />
                </Link>
              </li>
            ))}
          </S.List>
        )}
      </QueryRequest>
    </div>
  );
};

type tSearchProps = {
  t: TFunction;
  className?: string;
};
const Search = (props: tSearchProps) => {
  const history = useHistory();
  const generateSearchUrl = useSearchUrlGenerator();
  return (
    <S.SearchWrap className={props.className}>
      <AsyncSelect
        classNamePrefix="internal-cities-autocomplete-select"
        onChange={(v: { value: string }) => {
          if (!v) return;
          const url = generateSearchUrl({
            query: {},
            params: {
              loc: v.value,
            },
          });
          history.push(url);
        }}
        noOptionsMessage={() => props.t("common:empty")}
        cacheOptions
        placeholder={props.t(ns + "search_input_placeholder")}
        loadingMessage={() => props.t("common:loading")}
        loadOptions={async (input: string) => {
          const { items } = await CitiesAPI.autocomplete({ query: input });
          return items.map((i) => ({ value: i.slug, label: i.name }));
        }}
      />
      <SearchIcon
        css={`
          position: absolute;
          right: 5px;
          top: 50%;
          transform: translateY(-50%);
          transition: opacity 0.35s ease-in-out;
          color: var(--font-color-accent);
        `}
        color="currentColor"
      />
    </S.SearchWrap>
  );
};

export default memo(HighlightedCities);
